export default function BonitaSpringsDestinationsSection() {
  const stats = [
    {
      value: "55%",
      label: "of Bonita Springs requests are Caribbean cruise/Bahamas boating or Canadian snowbird return trips with under 3 weeks notice",
    },
    {
      value: "RSW",
      label: "Southwest Florida International is the primary airport — 20–25 minutes north with growing seasonal international routes",
    },
    {
      value: "Nov–Apr",
      label: "peak travel season — snowbird arrivals, cruise bookings, and Bahamas fishing trips converge",
    },
    {
      value: "36-hour",
      label: "shortest emergency window we've accommodated for a Bonita Springs traveler — a Bahamas fishing tournament departure",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "Bimini, Grand Bahama, the Abacos, and the Eastern Caribbean are not exotic destinations for many Bonita Springs residents — they are fishing grounds, boating destinations, and weekend escapes. The boating community makes regular crossings, while cruise passengers depart from Tampa, Fort Lauderdale, and Miami for week-long Caribbean itineraries. The proximity means spontaneous bookings are common, creating last-minute passport urgency when a document is discovered expired.",
      urgency: "Boating trips can be planned in 2–7 days when weather windows open — the shortest notice of any Bonita Springs travel pattern",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Montreal, Ottawa, and Vancouver draw Bonita Springs' substantial Canadian snowbird population for summer returns, family visits, and regional exploration. Many Canadian residents of Bonita Springs maintain dual-season lives, spending winters in Florida and summers in Ontario or Quebec. Seasonal direct flights from RSW to Toronto and Montreal make the journey convenient — but only with a valid passport.",
      urgency: "Summer return trips are planned in April–May — passport discoveries happen just as snowbirds are preparing to head north",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe",
      context: "London, Paris, Rome, Amsterdam, and the Mediterranean ports draw Bonita Springs retirees for river cruises, cultural tours, and extended summer vacations. European river cruises on the Danube, Rhine, and Seine are particularly popular among the affluent retirement community, often booked 6–12 months in advance through travel agents. The Schengen Area's three-month passport validity requirement is strictly enforced, and many airlines apply a six-month standard.",
      urgency: "European trips are planned 6–12 months ahead — but passport discoveries often happen 2–4 weeks before departure when documents are retrieved from storage",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, Dublin, and the Scottish Highlands attract Bonita Springs golf enthusiasts and heritage travelers. Scotland and Ireland golf trips are a staple of the retirement travel calendar, with groups of friends booking multi-course itineraries months in advance. The UK's six-month passport validity requirement is straightforward but unforgiving — airlines at RSW and connecting hubs enforce it strictly.",
      urgency: "Golf trips are booked in winter for spring/summer departure — passport checks should happen at booking, not in the final weeks",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, Puerto Vallarta, and Cabo San Lucas draw Bonita Springs residents for winter escapes, fishing trips, and resort vacations. Mexico is a particularly attractive destination during Bonita Springs' brief cool season in January and February, when a week in the Yucatan offers a warmer alternative to even Florida's occasional cold fronts. RSW offers connecting flights through Atlanta, Houston, and Dallas.",
      urgency: "Mexico winter trips are booked in October–November — right during the pre-holiday passport processing surge",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Panama Canal & Central America",
      context: "Panama Canal cruises, Costa Rica eco-tours, and Belize diving trips attract Bonita Springs' adventurous retirees and nature enthusiasts. These itineraries often depart from Florida ports or fly from RSW with connections through Miami, Houston, or Panama City. The region's growing popularity among Southwest Florida retirees means these trips are increasingly common — and passport requirements are strict for every port of call.",
      urgency: "Panama Canal cruises are booked 9–12 months in advance — but passport validity is often overlooked until final payment or boarding documents arrive",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Bonita Springs Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Bonita Springs
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Bonita Springs travelers head to the Bahamas, Caribbean, Canada, Europe, and beyond for a mix of boating adventures, cruise vacations, snowbird returns, and retirement tours. These are the destinations that create the most passport urgency for our Bonita Springs clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Southwest Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Naples", href: "/naples" },
              { name: "Fort Myers", href: "/fort-myers" },
              { name: "Sarasota", href: "/sarasota" },
              { name: "Tampa", href: "/tampa" },
              { name: "West Palm Beach", href: "/west-palm-beach" },
              { name: "Boca Raton", href: "/boca-raton" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}